import { CashOrThreeMonthEnum, FixingDateEvent, PricePointEnum, WhoFixesDateEnum } from 'api';
import {
  AutocompleteField,
  DateField,
  SelectField,
  type SelectOption,
  TextField,
  useFormContext,
  useWatch,
} from 'form';
import { useEffect, useState } from 'react';
import { createFilterOptions } from 'ui';

import { OrderFormVariant } from '~/orders/contexts/OrderFormVariant';
import { UpdateFormValues } from '~/orders/pages/PurchaseOrderUpdate/PurchaseOrderUpdate';
import { SalesOrderUpdateFormValues } from '~/orders/pages/SalesOrderUpdate/SalesOrderUpdate';

import { CreateFormValues } from '../../pages/PurchaseOrderCreate/PurchaseOrderCreate';

const INDEX_OPTIONS = [
  { label: 'LME Aluminium', value: 'LME Aluminium' },
  { label: 'LME Aluminium + Midwest Premium', value: 'LME Aluminium + Midwest Premium' },
];

const WHO_FIXES_DATE_OPTIONS: SelectOption[] = [
  { label: 'Customer', value: WhoFixesDateEnum.CUSTOMER },
  { label: 'Safi', value: WhoFixesDateEnum.SAFI },
  { label: 'Fixed Date', value: WhoFixesDateEnum.FIXED_DATE },
];

const FIXING_DATE_OPTIONS: SelectOption[] = [
  { label: 'Loading', value: FixingDateEvent.LOADING },
  { label: 'Port arrival', value: FixingDateEvent.PORT_ARRIVAL },
  { label: 'Site delivery', value: FixingDateEvent.SITE_DELIVERY },
  { label: 'Bill of Lading issue date', value: FixingDateEvent.BOL_ISSUE_DATE },
  { label: 'Port of departure', value: FixingDateEvent.PORT_DEPARTURE },
];

const CASH_OR_3MONTH_AVERAGE_OPTIONS: SelectOption[] = [
  { label: 'Cash', value: CashOrThreeMonthEnum.CASH },
  { label: '3-month average', value: CashOrThreeMonthEnum.THREE_MONTH_AVERAGE },
  { label: 'Lowest of the two', value: CashOrThreeMonthEnum.LOWEST },
];

const PRICEPOINT_OPTIONS = [
  { label: 'Open', value: PricePointEnum.OPEN },
  { label: 'Close', value: PricePointEnum.CLOSE },
  { label: 'Low', value: PricePointEnum.LOW },
  { label: 'High', value: PricePointEnum.HIGH },
  { label: 'Volume-weighted average', value: PricePointEnum.VWAP },
];

type Props = {
  formVariant: OrderFormVariant;
  defaultValues?: SalesOrderUpdateFormValues | UpdateFormValues;
};

export const VariablePricingFields = ({ defaultValues, formVariant }: Props) => {
  const [marketIndex, setMarketIndex] = useState<SelectOption | null>(INDEX_OPTIONS[0]);

  const { setValue } = useFormContext<CreateFormValues>();
  const { who_fixes_date: whoFixesDate } = useWatch<CreateFormValues>();

  const filter = createFilterOptions<SelectOption>();

  const defaultValue = defaultValues?.index ? { label: defaultValues.index, value: defaultValues.index } : null;

  // This is a hack to set the initial value for the form as for some reason when defaultValue is passed into Autocomplete it's not set
  useEffect(() => {
    setValue('index', defaultValue?.value ?? (marketIndex?.value as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AutocompleteField<SelectOption>
        // @ts-expect-error freeSolo is hardcoded to be false in our types for some reason
        freeSolo
        label="Index"
        name="index"
        options={INDEX_OPTIONS}
        required
        value={defaultValue ?? marketIndex}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        onSelectOption={(newValue: SelectOption | null) => {
          setMarketIndex(newValue);
        }}
        getOptionLabel={(option: SelectOption | string): string => {
          if (typeof option === 'string') {
            return option;
          }

          if (option.label.startsWith('Use')) {
            return option.value as string;
          }

          return option.label;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              {option.label}
            </li>
          );
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option.value);
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              label: `Use ${inputValue} as custom index`,
              value: inputValue,
            });
          }

          return filtered;
        }}
      />
      <TextField label="Price as percentage of the index" name="price_as_percentage_of_index" type="number" required />

      <SelectField
        label="Cash or 3-month average"
        name="cash_or_three_month"
        options={CASH_OR_3MONTH_AVERAGE_OPTIONS}
        required
      />

      <SelectField label="Who fixes the date" name="who_fixes_date" options={WHO_FIXES_DATE_OPTIONS} required />

      {whoFixesDate === WhoFixesDateEnum.CUSTOMER || whoFixesDate === WhoFixesDateEnum.SAFI ? (
        <>
          <SelectField
            label="Fixing date event - start"
            name="fixing_date_start_event"
            options={FIXING_DATE_OPTIONS}
            required
          />

          <TextField label="Fixing date offset - start" name="fixing_date_start_offset" type="number" required />

          <SelectField
            label="Fixing date event - end"
            name="fixing_date_end_event"
            options={FIXING_DATE_OPTIONS}
            required
          />

          <TextField label="Fixing date offset - end" name="fixing_date_end_offset" type="number" required />
        </>
      ) : null}

      {whoFixesDate === WhoFixesDateEnum.FIXED_DATE ? (
        <DateField label="Fixing date" name="fixing_date" required />
      ) : null}

      <SelectField label="Price point" name="price_point" type="number" required options={PRICEPOINT_OPTIONS} />

      {formVariant === 'update' ? (
        <>
          <TextField type="number" label="Fixed price" name="fixed_price" />
          <DateField label="Fixed date" name="date_fixed" pickerTestId="fixed-date-button" />
        </>
      ) : null}
    </>
  );
};
